import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import { useInput } from '@/checkout-instore-legacy/commons/customHooks'
import Button from '@vtex/styleguide/lib/Button'
import Input from '@vtex/styleguide/lib/Input'
import isEmpty from 'lodash/isEmpty'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import { validateEmail } from '@/checkout-instore-legacy/utils/EmailUtils'
import {
  validateCPF,
  maskCPF,
  clearMask,
} from '@/checkout-instore-legacy/utils/CPFUtils'
import { handleInputMask } from '@/checkout-instore-legacy/utils/inputUtils'
import { shouldDoLocalInputInvoice } from '@/checkout-instore-legacy/utils/printUtils'
import BackHeader from '@/checkout-instore-legacy/components/Commons/BackHeader'
import Loader from '@/checkout-instore-legacy/components/Commons/Loader/index'
import {
  createGiftCardConfirmationModalContext,
  createCancelModalContext,
} from '@/checkout-instore-legacy/returnExchange/utils'

const isCPF = (value) => value && !isNaN(value.substr(0, 3))
const maskDocument = (data) => (isCPF(data) ? maskCPF(data) : data)

const ExchangeClient = ({
  router,
  intl,
  order,
  clearReturnExchange,
  openRootModalWithDialog,
  generateGiftcard,
  clearAllErrors,
  giftcardLoading,
  setGiftcardLoading,
}) => {
  const cpfInput = useRef(null)
  useEffect(() => {
    setGiftcardLoading(false)
  }, [setGiftcardLoading])

  const [email, handleEmailChange] = useInput('')
  const [cpf, handleCpfChange] = useInput('')
  const orderId = order && order.orderId

  const handleCPFOnChange = useCallback(
    (e) => {
      handleCpfChange(e)
      handleInputMask(cpf, e.target.value, maskDocument, cpfInput.current)
    },
    [cpf, handleCpfChange]
  )

  const handleSubmitOnClick = useCallback(() => {
    const modalContext = createGiftCardConfirmationModalContext(() => {
      generateGiftcard({ email, document: clearMask(cpf) })
    }, intl)
    openRootModalWithDialog(modalContext)
  }, [cpf, email, generateGiftcard, intl, openRootModalWithDialog])

  const handleCancel = useCallback(() => {
    const modalContext = createCancelModalContext(() => {
      router.push('/')
      clearReturnExchange()
    }, intl)
    openRootModalWithDialog(modalContext)
  }, [clearReturnExchange, intl, openRootModalWithDialog, router])

  const handleBack = useCallback(() => {
    clearAllErrors()
    router.push(`/return-exchange/${orderId}`)
  }, [clearAllErrors, orderId, router])

  if (!orderId) {
    return null
  }

  const isExchangeFlow = true
  const isLocalInput = shouldDoLocalInputInvoice(isExchangeFlow)

  return (
    <React.Fragment>
      <Loader
        loading={giftcardLoading}
        badLoadingTime={isLocalInput ? 30 : undefined}
        maxLoadingTime={isLocalInput ? 60 : undefined}
      />
      <div className="flex flex-auto flex-column items-center bg-muted-5 c-on-base pt8 pb5 overflow-auto">
        <div className="flex flex-column w-50">
          <BackHeader
            messageId="returnExchange.client.back"
            onClick={handleBack}
          />
          <div className="flex flex-row justify-between items-center">
            <span className="t-heading-2 pt2">
              <FormattedMessage id="returnExchange.client.title" />
            </span>
          </div>
          <div className="pt7">
            <div>
              <Input
                label={'Email'}
                type="email"
                onChange={handleEmailChange}
                value={email}
                error={!validateEmail(email) && !isEmpty(email)}
              />
            </div>
            <div className="pt6">
              <Input
                label={'CPF'}
                type="tel"
                maxLength={14}
                ref={cpfInput}
                onChange={handleCPFOnChange}
                value={maskCPF(cpf)}
                error={!validateCPF(cpf) && !isEmpty(cpf)}
              />
            </div>
          </div>
          <div className="self-end pt7">
            <Button variation="tertiary" onClick={handleCancel}>
              <FormattedMessage id="returnExchange.client.button.cancel" />
            </Button>
            <Button
              variation="primary"
              disabled={!validateEmail(email) || !validateCPF(cpf)}
              onClick={handleSubmitOnClick}
            >
              <FormattedMessage id="returnExchange.client.button.giftcard" />
            </Button>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

ExchangeClient.propTypes = {
  order: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  router: PropTypes.any,
  giftcardLoading: PropTypes.bool,
  clearReturnExchange: PropTypes.func.isRequired,
  generateGiftcard: PropTypes.func.isRequired,
  openRootModalWithDialog: PropTypes.func.isRequired,
  setGiftcardLoading: PropTypes.func.isRequired,
  clearAllErrors: PropTypes.func.isRequired,
}

export default withRouter(injectIntl(ExchangeClient))
